import React, { useEffect, useMemo, useState } from "react";
import "./knowledge.css";
import {
  GET_AI_AGENTS,
  POST_WEBSITE_KNOWLEDGE,
} from "../../api/aiKnowledge.service";
import { useLayout } from "../../context/LayoutContext";

const AgentKnowledge = () => {
  const { toast } = useLayout();
  const [agent, setAgent] = useState("");
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAgents = async () => {
    setLoadingAgents(true);
    setAgentError("");
    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.data?.rows || [];
      const mapped = rows.map((item) => ({
        id: String(item.id),
        name: item.agentName,
      }));
      setAgents(mapped);
      if (mapped.length > 0) {
        setAgent(mapped[0].id);
        setAgentSearch(mapped[0].name);
      }
    } catch (err) {
      setAgentError(err?.response?.data?.message || "Failed to load agents.");
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  const websitePreviewUrl = useMemo(() => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  }, [url]);

  const handleSave = async () => {
    if (!agent) {
      toast.current?.show({
        severity: "warn",
        detail: "Please select an agent.",
      });
      return;
    }
    if (!websitePreviewUrl) {
      toast.current?.show({
        severity: "warn",
        detail: "Please enter a valid website URL.",
      });
      return;
    }

    setSaving(true);

    try {
      const resp = await POST_WEBSITE_KNOWLEDGE({
        KnowledgeUrl: websitePreviewUrl,
        agentId: agent,
        sourceName,
      });
      const successMessage =
        resp?.data?.message || resp?.message || "Knowledge saved successfully.";
      toast.current?.show({
        severity: "success",
        detail: successMessage,
      });
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save knowledge.";
      toast.current?.show({
        severity: "error",
        detail: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="knowledge__card">
      <div className="knowledge__grid">
        <div className="knowledge__panel">
          <label className="knowledge__label">Agent</label>
          <div className="knowledge__combobox">
            <input
              className="knowledge__input"
              placeholder="Search agent..."
              value={agentSearch}
              onChange={(event) => {
                setAgentSearch(event.target.value);
                setAgentDropdownOpen(true);
              }}
              onFocus={() => setAgentDropdownOpen(true)}
              onBlur={() => {
                setTimeout(() => setAgentDropdownOpen(false), 120);
              }}
              disabled={loadingAgents || agents.length === 0}
            />
            {agentDropdownOpen && !loadingAgents && agents.length > 0 && (
              <div className="knowledge__dropdown">
                {agents
                  .filter((item) =>
                    item.name
                      .toLowerCase()
                      .includes(agentSearch.toLowerCase().trim()),
                  )
                  .map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`knowledge__dropdown-item${
                        item.id === agent
                          ? " knowledge__dropdown-item--active"
                          : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setAgent(item.id);
                        setAgentSearch(item.name);
                        setAgentDropdownOpen(false);
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
              </div>
            )}
          </div>
          {agentError ? (
            <div className="knowledge__hint">{agentError}</div>
          ) : null}

          <label className="knowledge__label">Source Name</label>
          <input
            className="knowledge__input"
            placeholder="Source name"
            value={sourceName}
            onChange={(event) => setSourceName(event.target.value)}
          />

          <label className="knowledge__label">Website URL</label>
          <input
            className="knowledge__input"
            placeholder="https://example.com"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <div className="knowledge__hint">
            Enter a public URL to preview the website.
          </div>

          <div className="knowledge__inline-actions">
            <button
              type="button"
              className="knowledge__btn knowledge__btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="knowledge__preview">
          <div className="knowledge__preview-header">Preview</div>
          {!websitePreviewUrl ? (
            <div className="knowledge__empty">
              Enter a URL to preview the website.
            </div>
          ) : (
            <div className="knowledge__desktop-frame">
              <div className="knowledge__desktop-toolbar">
                <span className="knowledge__desktop-dot knowledge__desktop-dot--red" />
                <span className="knowledge__desktop-dot knowledge__desktop-dot--yellow" />
                <span className="knowledge__desktop-dot knowledge__desktop-dot--green" />
                <div className="knowledge__desktop-url">
                  {websitePreviewUrl}
                </div>
              </div>
              <div className="knowledge__desktop-viewport">
                <div className="knowledge__desktop-scale">
                  <iframe
                    title="Website Preview"
                    className="knowledge__desktop-iframe"
                    src={websitePreviewUrl}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="knowledge__footer"></div>
    </div>
  );
};

export default AgentKnowledge;
